import { useState, useRef } from "react";
import "./ApplyForm.css";
import { trackEvent } from "../utils/mixpanel";
import { EVENTS } from "../utils/mixpanelEvents";

function ApplyForm() {
  const resumeInputRef = useRef(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    dob: "",
    yearExperience: "",
    role: "",
    location: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone must be 10 digits";
    }
    if (!formData.dob.trim()) newErrors.dob = "Date of birth is required";
    if (!formData.yearExperience)
      newErrors.yearExperience = "Select experience";
    if (!formData.role) newErrors.role = "Select a role";
    if (!resumeFile) newErrors.resume = "Upload your resume";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResumeFile(file);
    if (errors.resume) {
      setErrors((prev) => ({ ...prev, resume: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent(EVENTS.FORM_SUBMIT_CLICK, { role: formData.role, experience: formData.yearExperience });
    if (!validateForm()) return;

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-");
      return `${day}-${month}-${year}`;
    };

    try {
      setSubmitting(true);
      const myHeaders = new Headers();
      myHeaders.append("accept", "application/json, text/plain, */*");

      const formdata = new FormData();
      if (resumeFile) {
        formdata.append("file", resumeFile);
      }

      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        dob: formatDate(formData.dob),
        yearExperience: formData.yearExperience,
        role: formData.role,
        monthExperience: "",
      };

      formdata.append("json", JSON.stringify(payload));

      const response = await fetch(
        "https://uatsvcl-api.finncub.com/misc/svclJobApplication/applyForJob",
        { method: "POST", headers: myHeaders, body: formdata },
      );

      if (response.ok) {
        trackEvent(EVENTS.FORM_SUBMITTED, { role: formData.role, experience: formData.yearExperience });
        setSuccess(true);
        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          dob: "",
          yearExperience: "",
          role: "",
          location: "",
        });
        setResumeFile(null);
        if (resumeInputRef.current) resumeInputRef.current.value = "";
      } else {
        console.error("Submit failed", await response.text());
      }
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="apply-form">
      <h2>Apply here</h2>
      {success && (
        <p className="success-msg">Application submitted successfully!</p>
      )}
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-field">
            <input
              type="text"
              name="fullName"
              placeholder="Full name"
              value={formData.fullName}
              onChange={handleChange}
            />
            {errors.fullName && (
              <span className="error">{errors.fullName}</span>
            )}
          </div>
          <div className="form-field">
            <input
              type="email"
              name="email"
              placeholder="Email address"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>
          <div className="form-field">
            <input
              type="tel"
              name="phoneNumber"
              placeholder="Phone number"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
            {errors.phoneNumber && (
              <span className="error">{errors.phoneNumber}</span>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <input
              type="date"
              name="dob"
              placeholder="Date of birth"
              value={formData.dob}
              onChange={handleChange}
            />
            {errors.dob && <span className="error">{errors.dob}</span>}
          </div>
          <div className="form-field">
            <select
              name="yearExperience"
              value={formData.yearExperience}
              onChange={handleChange}
            >
              <option value="" disabled>
                Work Experience
              </option>
              <option value="0-1">0-1 years</option>
              <option value="1-3">1-3 years</option>
              <option value="3-5">3-5 years</option>
              <option value="5+">5+ years</option>
            </select>
            {errors.yearExperience && (
              <span className="error">{errors.yearExperience}</span>
            )}
          </div>
          <div className="form-field">
            <select name="role" value={formData.role} onChange={handleChange}>
              <option value="" disabled>
                Role
              </option>
              <option value="engineering">Engineering</option>
              <option value="operations">Operations</option>
              <option value="finance">Finance</option>
              <option value="other">Other</option>
            </select>
            {errors.role && <span className="error">{errors.role}</span>}
          </div>
        </div>
        <div className="form-row form-row-last">
          <div className="form-field">
            <input
              type="text"
              name="location"
              placeholder="Location"
              value={formData.location}
              onChange={handleChange}
            />
          </div>
          <div className="form-field file-upload">
            <span className="file-label">Resume</span>
            <label className="file-btn">
              Choose File
              <input
                type="file"
                ref={resumeInputRef}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                hidden
              />
            </label>
            <span className="file-name">
              {resumeFile ? resumeFile.name : "No file selected"}
            </span>
            {errors.resume && <span className="error">{errors.resume}</span>}
          </div>
          <div className="form-field submit-field">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"} ✈
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

export default ApplyForm;
